import React, { useState, useEffect } from "react";
import WalletModal from "./WalletModal";
import ik2Image from "../assets/jhh.png";

const Navbar = ({ backendStatus, user, onWalletConnect }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      const sections = ['home', 'ecosystem', 'claim', 'community'];
      const currentSection = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (currentSection) {
        setActiveSection(currentSection);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (sectionId) => {
    setActiveSection(sectionId);
    setIsMobileMenuOpen(false);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const handleWalletConnection = async (walletData) => {
    if (onWalletConnect) {
      const result = await onWalletConnect(walletData);
      if (result.success) {
        setIsModalOpen(false);
      }
      return result;
    }
    return { success: false, error: 'No connection handler provided' };
  };

  const isButtonDisabled = backendStatus === 'error' || backendStatus === 'disconnected';
  
  const getButtonText = () => {
    if (user) return "Connected";
    if (backendStatus === 'checking') return "Loading...";
    return "Connect Wallet";
  };

  return (
    <>
      {process.env.NODE_ENV === 'development' && backendStatus !== 'connected' && (
        <div className="bg-yellow-500/20 text-yellow-200 text-center py-1 px-4 text-xs">
          ⚠️ Development: Backend {backendStatus}
        </div>
      )}

      <header className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'py-3 bg-gray-900/95 backdrop-blur-md border-b border-gray-800 shadow-lg' 
          : 'py-4 bg-gradient-to-b from-gray-900 to-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-10 sm:w-16 sm:h-14 rounded-xl overflow-hidden border-2 border-white/30 bg-gray-900 flex items-center justify-center">
              <img 
                src={ik2Image} 
                alt="EcoChain Logo" 
                className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-white text-sm font-bold">X</div>';
                }}
              />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                ECOCHAIN
              </h1>
              <div className="flex items-center space-x-2 mt-0.5">
                <span className="text-xs text-gray-300 font-medium px-2 py-0.5 rounded-full bg-gray-800/50">
                  White Blockchain
                </span>
              </div>
            </div>
            <div className="sm:hidden">
              <h1 className="text-lg font-bold text-white">
                ECOCHAIN
              </h1>
            </div>
          </div>

          <nav className="hidden md:flex items-center space-x-1">
            {['home', 'ecosystem', 'claim', 'community'].map((section) => (
              <button
                key={section}
                onClick={() => handleNavClick(section)}
                className={`px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                  activeSection === section
                    ? 'text-white bg-gray-800/70'
                    : 'text-gray-300 hover:text-white hover:bg-gray-800/50'
                }`}
              >
                {section.charAt(0).toUpperCase() + section.slice(1)}
              </button>
            ))}
          </nav>

          <div className="flex items-center space-x-3 sm:space-x-4">
            <button 
              className={`px-4 py-2.5 sm:px-6 sm:py-3 font-semibold rounded-xl transition-all duration-300 flex items-center space-x-2 ${
                isButtonDisabled
                  ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-white to-gray-300 hover:from-gray-200 hover:to-gray-400 text-gray-900 shadow-lg hover:shadow-white/25'
              }`}
              onClick={() => !isButtonDisabled && setIsModalOpen(true)}
              disabled={isButtonDisabled}
              title={isButtonDisabled ? "Service temporarily unavailable. Please try again later." : ""}
            >
              {backendStatus === 'checking' ? (
                <>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span className="hidden sm:inline">Loading...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span className="text-sm sm:text-base">{getButtonText()}</span>
                </>
              )}
            </button>

            <button 
              className="md:hidden text-gray-300 hover:text-white p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-gray-900/95 backdrop-blur-md border-t border-gray-800">
            <div className="px-4 py-3 space-y-2">
              {['home', 'ecosystem', 'claim', 'community'].map((section) => (
                <button
                  key={section}
                  onClick={() => handleNavClick(section)}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 ${
                    activeSection === section
                      ? 'text-white bg-gray-800/70'
                      : 'text-gray-300 hover:text-white hover:bg-gray-800/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">
                      {section.charAt(0).toUpperCase() + section.slice(1)}
                    </span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </header>

      <WalletModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        onWalletConnect={handleWalletConnection}
        backendStatus={backendStatus}
      />
    </>
  );
};

export default Navbar;